import React, { useEffect, useState } from "react";
import axios from "axios";
import { Cookie, Shield, BarChart3, Settings, Globe, ArrowLeft, Check, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAST_UPDATE = "10 de abril de 2026";

function LegalHeader({ company }) {
  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <a href="/superpoderes" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft size={18} className="text-gray-600" />
          </a>
          <div className="w-8 h-8 rounded-lg bg-red-600 flex items-center justify-center">
            <Cookie size={15} className="text-white" />
          </div>
          <span className="font-semibold text-gray-900">{company?.app_name || "OkBot"}</span>
        </div>
        <nav className="hidden md:flex items-center gap-6 text-sm">
          <a href="/terminos" className="text-gray-500 hover:text-gray-900 transition-colors">Términos</a>
          <a href="/privacidad" className="text-gray-500 hover:text-gray-900 transition-colors">Privacidad</a>
          <a href="/aviso-legal" className="text-gray-500 hover:text-gray-900 transition-colors">Aviso Legal</a>
          <a href="/cookies" className="text-red-600 font-medium border-b-2 border-red-600 pb-0.5">Cookies</a>
        </nav>
      </div>
    </header>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200">{title}</h2>
      {children}
    </section>
  );
}

function CookieTypeCard({ icon: Icon, title, color, badge, children }) {
  return (
    <div className="border border-gray-200 rounded-2xl p-5 bg-white shadow-sm">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={18} className="text-white" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{title}</h3>
            {badge && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${badge === "Siempre activas" ? "bg-green-100 text-green-700" : "bg-orange-100 text-orange-700"}`}>
                {badge}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="text-gray-600 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function CookieRow({ name, purpose, duration, type }) {
  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
      <td className="px-4 py-3 font-mono text-xs text-gray-800 font-medium">{name}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{purpose}</td>
      <td className="px-4 py-3 text-sm text-gray-500">{duration}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
          type === "Esencial" ? "bg-green-100 text-green-700" :
          type === "Analítica" ? "bg-blue-100 text-blue-700" :
          type === "Funcional" ? "bg-purple-100 text-purple-700" :
          "bg-orange-100 text-orange-700"
        }`}>{type}</span>
      </td>
    </tr>
  );
}

const CookiesPage = () => {
  const [company, setCompany] = useState(null);
  const [consentGiven, setConsentGiven] = useState(() => localStorage.getItem("okbot_cookie_consent") === "true");

  useEffect(() => {
    axios.get(`${API}/admin/legal/public-info`).then(r => setCompany(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const acceptAll = () => {
    localStorage.setItem("okbot_cookie_consent", "true");
    setConsentGiven(true);
  };

  const rejectOptional = () => {
    localStorage.setItem("okbot_cookie_consent", "false");
    setConsentGiven(false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <LegalHeader company={company} />

      {/* Hero */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-red-600 flex items-center justify-center">
              <Cookie size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Política de Cookies</h1>
              <p className="text-gray-500 text-sm mt-0.5">Última actualización: {LAST_UPDATE}</p>
            </div>
          </div>
          <p className="text-gray-600 max-w-2xl text-base leading-relaxed">
            Esta política explica qué son las cookies, cómo las usamos en {company?.app_name || "OkBot"}, 
            y cómo puedes controlar su uso. Cumplimos con la Ley Federal de Protección de Datos Personales 
            en Posesión de los Particulares (LFPDPPP) de México.
          </p>
        </div>
      </div>

      {/* Banner de consentimiento activo */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className={`rounded-2xl p-5 border-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${
          consentGiven ? "bg-green-50 border-green-200" : "bg-orange-50 border-orange-200"
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${consentGiven ? "bg-green-500" : "bg-orange-400"}`}>
              {consentGiven ? <Check size={18} className="text-white" /> : <Cookie size={18} className="text-white" />}
            </div>
            <div>
              <p className={`font-semibold text-sm ${consentGiven ? "text-green-800" : "text-orange-800"}`}>
                {consentGiven ? "Preferencias guardadas — cookies opcionales activas" : "Preferencias actuales: solo cookies esenciales"}
              </p>
              <p className={`text-xs mt-0.5 ${consentGiven ? "text-green-600" : "text-orange-600"}`}>
                Puedes cambiar tus preferencias en cualquier momento desde aquí.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={rejectOptional} className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-50 transition-colors flex items-center gap-1.5">
              <X size={14} />Solo esenciales
            </button>
            <button onClick={acceptAll} className="px-4 py-2 bg-red-600 text-white rounded-xl text-sm font-medium hover:bg-red-700 transition-colors flex items-center gap-1.5">
              <Check size={14} />Aceptar todas
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-16">

        <Section title="1. ¿Qué son las cookies?">
          <p className="text-gray-600 leading-relaxed mb-3">
            Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo cuando visitas un sitio web. 
            Se utilizan ampliamente para hacer que los sitios web funcionen de manera más eficiente y proporcionar 
            información a los propietarios del sitio.
          </p>
          <p className="text-gray-600 leading-relaxed">
            Las cookies pueden ser "propias" (establecidas por nuestro dominio) o "de terceros" (establecidas por 
            dominios externos). También pueden ser de sesión (se eliminan al cerrar el navegador) o persistentes 
            (permanecen por un tiempo determinado).
          </p>
        </Section>

        <Section title="2. Tipos de cookies que utilizamos">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CookieTypeCard icon={Shield} title="Cookies Esenciales" color="bg-green-500" badge="Siempre activas">
              Necesarias para el funcionamiento básico de la plataforma. Sin estas cookies, servicios como la 
              autenticación, seguridad y preferencias no funcionarían correctamente. No pueden desactivarse.
            </CookieTypeCard>
            <CookieTypeCard icon={BarChart3} title="Cookies Analíticas" color="bg-blue-500" badge="Opcionales">
              Nos ayudan a entender cómo los usuarios interactúan con la plataforma — qué módulos usan más, 
              rutas de navegación, y tiempos de sesión. Todos los datos son anónimos y agregados.
            </CookieTypeCard>
            <CookieTypeCard icon={Settings} title="Cookies Funcionales" color="bg-purple-500" badge="Opcionales">
              Recuerdan tus preferencias personales: tema (claro/oscuro), idioma, configuración de módulos, 
              y personalizaciones de la interfaz para mejorar tu experiencia.
            </CookieTypeCard>
            <CookieTypeCard icon={Globe} title="Cookies de Terceros" color="bg-orange-500" badge="Opcionales">
              Proveedores externos como servicios de análisis o soporte técnico pueden establecer cookies 
              bajo sus propias políticas de privacidad. Listamos todos los terceros activos abajo.
            </CookieTypeCard>
          </div>
        </Section>

        <Section title="3. Cookies específicas que usamos">
          <div className="overflow-x-auto rounded-2xl border border-gray-200">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cookie</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Propósito</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Duración</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Tipo</th>
                </tr>
              </thead>
              <tbody>
                <CookieRow name="okbot_user" purpose="Mantiene tu sesión activa y datos de autenticación" duration="30 días" type="Esencial" />
                <CookieRow name="okbot_admin_token" purpose="Token de autenticación para administradores" duration="Sesión" type="Esencial" />
                <CookieRow name="okbot_cookie_consent" purpose="Guarda tus preferencias de consentimiento de cookies" duration="1 año" type="Esencial" />
                <CookieRow name="theme" purpose="Recuerda tu preferencia de tema claro/oscuro" duration="1 año" type="Funcional" />
                <CookieRow name="okbot_chat_*" purpose="Historial de conversaciones con el asistente IA" duration="Sesión" type="Funcional" />
                <CookieRow name="_ga" purpose="Google Analytics — seguimiento de visitas anónimas" duration="2 años" type="Analítica" />
                <CookieRow name="_gid" purpose="Google Analytics — diferencia entre sesiones" duration="24 horas" type="Analítica" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="4. Cookies de terceros">
          <div className="space-y-3">
            {[
              { name: "Google Analytics", purpose: "Análisis de tráfico y comportamiento de usuarios (datos anónimos)", link: "https://policies.google.com/privacy" },
              { name: "Stripe", purpose: "Procesamiento de pagos seguros — cookies de seguridad antifraude", link: "https://stripe.com/privacy" },
              { name: "OpenRouter", purpose: "Procesamiento de consultas a modelos de IA", link: "https://openrouter.ai/privacy" },
            ].map(t => (
              <div key={t.name} className="flex items-start gap-4 p-4 bg-white border border-gray-200 rounded-xl">
                <div className="w-2 h-2 rounded-full bg-red-500 mt-2 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 text-sm">{t.name}</p>
                  <p className="text-gray-500 text-sm">{t.purpose}</p>
                </div>
                <a href={t.link} target="_blank" rel="noopener noreferrer" className="text-red-600 text-xs hover:underline flex-shrink-0">
                  Ver política →
                </a>
              </div>
            ))}
          </div>
        </Section>

        <Section title="5. Cómo controlar las cookies">
          <p className="text-gray-600 leading-relaxed mb-4">
            Además del panel de preferencias en la parte superior de esta página, puedes controlar las cookies 
            directamente desde la configuración de tu navegador:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { name: "Chrome", link: "https://support.google.com/chrome/answer/95647" },
              { name: "Firefox", link: "https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web" },
              { name: "Safari", link: "https://support.apple.com/es-mx/guide/safari/sfri11471/mac" },
              { name: "Edge", link: "https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" },
            ].map(b => (
              <a key={b.name} href={b.link} target="_blank" rel="noopener noreferrer"
                className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-xl hover:border-red-300 hover:shadow-sm transition-all group">
                <span className="font-medium text-gray-900 text-sm">{b.name}</span>
                <span className="text-red-600 text-xs group-hover:translate-x-0.5 transition-transform">Configurar →</span>
              </a>
            ))}
          </div>
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-xl">
            <p className="text-orange-800 text-sm">
              <strong>Aviso:</strong> Deshabilitar cookies esenciales puede afectar el funcionamiento de la 
              plataforma. Algunas funciones como el inicio de sesión pueden dejar de funcionar correctamente.
            </p>
          </div>
        </Section>

        <Section title="6. Base legal (México)">
          <p className="text-gray-600 leading-relaxed mb-3">
            El uso de cookies en México está regulado principalmente por:
          </p>
          <ul className="space-y-2 text-gray-600 text-sm">
            <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span>Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP)</li>
            <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span>Reglamento de la LFPDPPP</li>
            <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span>Lineamientos del Aviso de Privacidad emitidos por el INAI</li>
            <li className="flex items-start gap-2"><span className="text-red-500 mt-1">•</span>Ley Federal de Telecomunicaciones y Radiodifusión (LFTR)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            Las cookies analíticas y funcionales se procesan con base en tu consentimiento expreso, el cual 
            puedes revocar en cualquier momento desde esta página.
          </p>
        </Section>

        <Section title="7. Actualizaciones de esta política">
          <p className="text-gray-600 leading-relaxed">
            Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en nuestras 
            prácticas o por razones legales. Te notificaremos de cambios significativos mediante un aviso 
            destacado en la plataforma. La versión más reciente siempre estará disponible en esta página.
          </p>
        </Section>

        <Section title="8. Contacto">
          <p className="text-gray-600 leading-relaxed">
            Si tienes preguntas sobre nuestra política de cookies o quieres ejercer tus derechos ARCO, 
            contáctanos en:
          </p>
          <div className="mt-4 p-4 bg-gray-50 rounded-xl border border-gray-200 space-y-1 text-sm text-gray-700">
            <p><strong>{company?.company_name || "OkGo Tech S.A. de C.V."}</strong></p>
            <p>Email: <a href={`mailto:${company?.email || "hola@okgo.mx"}`} className="text-red-600 hover:underline">{company?.email || "hola@okgo.mx"}</a></p>
            {company?.phone && <p>Tel: {company.phone}</p>}
            {company?.address && <p>{company.address}</p>}
          </div>
        </Section>
      </div>

      <footer className="bg-white border-t border-gray-200 py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="flex flex-wrap justify-center gap-6 text-sm mb-4">
            <a href="/terminos" className="text-gray-500 hover:text-red-600 transition-colors">Términos de Servicio</a>
            <a href="/privacidad" className="text-gray-500 hover:text-red-600 transition-colors">Política de Privacidad</a>
            <a href="/aviso-legal" className="text-gray-500 hover:text-red-600 transition-colors">Aviso Legal</a>
            <a href="/cookies" className="text-red-600 font-medium">Cookies</a>
          </div>
          <p className="text-gray-400 text-sm">© 2026 {company?.company_name || "OkGo Tech S.A. de C.V."} — Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default CookiesPage;
